import { rootApi } from '../rootApi';

const excelExportQuery = (url, mapParams) => async (arg, _api, _extraOptions, baseQuery) => {
    const params = mapParams ? mapParams(arg) : undefined;
    const result = await baseQuery({
        url,
        method: 'GET',
        ...(params ? { params } : {})
    });

    if (result.error) {
        const data = result.error.data;
        const errorMessage = data && typeof data === 'object' ? data.errorMessage || data.message : undefined;
        if (errorMessage) {
            return { error: { status: result.error.status, data: { errorMessage } } };
        }
        return { error: result.error };
    }

    const response = result.data;
    if (response?.isSuccessful && response?.data) {
        const { fileBase64, fileName, contentType, recordCount, fileSize, isCompressed, compressionType } = response.data;
        return { data: { fileBase64, fileName, contentType, recordCount, fileSize, isCompressed, compressionType } };
    }

    return {
        error: {
            status: 'CUSTOM_ERROR',
            data: { errorMessage: response?.errorMessage || '' }
        }
    };
};

export const partsApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getWaybillShipments: builder.query({
            query: (params) => {
                const queryParams = {
                    PageNumber: params.PageNumber !== undefined ? params.PageNumber : 1,
                    PageSize: params.PageSize || 20
                };
                if (params.OrderNo) queryParams.OrderNo = params.OrderNo;
                if (params.WaybillNo) queryParams.WaybillNo = params.WaybillNo;
                if (params.EFaturaNo) queryParams.EFaturaNo = params.EFaturaNo;
                if (params.DealerNo) queryParams.DealerNo = params.DealerNo;
                if (params.DealerName) queryParams.DealerName = params.DealerName;

                return {
                    url: 'v1/parts',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && response.data.items && Array.isArray(response.data.items)) {
                        return {
                            items: response.data.items,
                            totalCount: response.data.totalCount,
                            count: response.count
                        };
                    }
                    if (response.data && Array.isArray(response.data)) {
                        return {
                            items: response.data.items,
                            totalCount: response.data.totalCount,
                            count: response.count
                        };
                    }
                    if (Array.isArray(response)) {
                        return {
                            items: response,
                            totalCount: response.length,
                            count: response.length
                        };
                    }
                }
                return {
                    items: [],
                    totalCount: 0
                };
            },
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    await queryFulfilled;
                } catch (error) {
                    console.error('getWaybillShipments failed', error);
                }
            },
            providesTags: ['WaybillShipment']
        }),
        getOrderBalances: builder.query({
            query: (params) => {
                const queryParams = {
                    PageNumber: params.PageNumber !== undefined ? params.PageNumber : 1,
                    PageSize: params.PageSize || 20
                };
                if (params.ShippingAddressId) queryParams.ShippingAddressId = params.ShippingAddressId;
                if (params.OrderNo) queryParams.OrderNo = params.OrderNo;
                if (params.DealerName) queryParams.DealerName = params.DealerName;

                return {
                    url: 'v1/parts/get-orderr-balances',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && response.data.items && Array.isArray(response.data.items)) {
                        return {
                            items: response.data.items,
                            totalCount: response.data.totalCount,
                            count: response.count
                        };
                    }
                    if (response.data && Array.isArray(response.data)) {
                        return {
                            items: response.data.items,
                            totalCount: response.data.totalCount,
                            count: response.count
                        };
                    }
                    if (Array.isArray(response)) {
                        return {
                            items: response,
                            totalCount: response.length,
                            count: response.length
                        };
                    }
                }
                return {
                    items: [],
                    totalCount: 0
                };
            },
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    await queryFulfilled;
                } catch (error) {
                    console.error('getOrderBalances failed', error);
                }
            },
            providesTags: ['OrderBalance']
        }),
        userSearchPart: builder.query({
            query: (params = {}) => ({
                url: 'v1/parts/search_parts',
                params
            }),
            providesTags: ['PartSearch']
        }),
        getSapInvoices: builder.query({
            query: ({ PageSize = 10, PageNumber = 1, InvoiceNo = '', efatura = '', Received } = {}) => {
                return {
                    url: `v1/parts/get-invoices`,
                    method: 'GET',
                    params: {
                        PageNumber,
                        PageSize,
                        ...(InvoiceNo ? { InvoiceNo } : {}),
                        ...(efatura ? { efatura } : {}),
                        ...(Received !== undefined ? { Received } : {})
                    }
                };
            },
            providesTags: (result, error, arg) => [{ type: 'GetSapInvoices', id: arg?.id }]
        }),
        getSapInvoiceDetail: builder.query({
            query: ({ InvoiceNo = '' }) => {
                return {
                    url: `v1/parts/get-invoice-detail`,
                    method: 'GET',
                    params: {
                        InvoiceNo
                    }
                };
            },
            providesTags: (result, error, arg) => [{ type: 'getSapInvoiceDetailQuery', id: arg?.id }]
        }),
        InternalOrderPickup: builder.mutation({
            query: (data) => ({
                url: 'v1/parts/internal-order-pickup',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['InternalOrderPickup']
        }),
        getExternalInvoices: builder.query({
            query: ({ PageSize = 10, PageNumber = 1, InvoiceNo = '', Received } = {}) => {
                return {
                    url: `v1/parts/get-external-invoices`,
                    method: 'GET',
                    params: {
                        PageNumber,
                        PageSize,
                        ...(InvoiceNo ? { InvoiceNo } : {}),
                        ...(Received !== undefined ? { Received } : {})
                    }
                };
            },
            providesTags: (result, error, arg) => [{ type: 'ExternalInvoices', id: arg?.id }]
        }),
        getExternalInvoiceDetail: builder.query({
            query: ({ LanguageId, InvoiceNo }) => {
                return {
                    url: `v1/parts/get-external-invoice-detail`,
                    method: 'GET',
                    params: {
                        LanguageId,
                        ...(InvoiceNo ? { InvoiceNo } : {})
                    }
                };
            },
            providesTags: (result, error, arg) => [{ type: 'ExternalInvoiceDetail', id: arg?.InvoiceNo || 'new' }]
        }),
        externalOrderPickup: builder.mutation({
            query: (data) => ({
                url: 'v1/parts/external-order-pickup',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['ExternalOrderPickup', 'ExternalInvoices']
        }),
        getDeliveryNotePickupList: builder.query({
            query: ({ PageSize = 10, PageNumber = 1, Dln = '', Received } = {}) => {
                return {
                    url: `v1/parts/delivery-note-pickup-list`,
                    method: 'GET',
                    params: {
                        PageNumber,
                        PageSize,
                        ...(Dln ? { Dln } : {}),
                        ...(Received !== undefined ? { Received } : {})
                    }
                };
            },
            providesTags: (result, error, arg) => [{ type: 'DeliveryNotePickupList', id: arg?.id }]
        }),
        getDeliveryNotePickupDetail: builder.query({
            query: ({ DeliveryNoteNumber = '' }) => {
                return {
                    url: `v1/parts/delivery-note-pickup-detail`,
                    method: 'GET',
                    params: {
                        DeliveryNoteNumber
                    }
                };
            },
            providesTags: (result, error, arg) => [{ type: 'DeliveryNotePickupDetail', id: arg?.id }]
        }),
        deliveryNotePickup: builder.mutation({
            query: (data) => ({
                url: 'v1/parts/delivery-note-pickup',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['DeliveryNotePickup', 'DeliveryNotePickupList']
        }),
        // External Item Description endpoints
        getExternalItemDescriptions: builder.query({
            query: (params = {}) => ({
                url: 'v1/parts/external-item-description-getall',
                method: 'GET',
                params: {
                    PageNumber: params.pageNumber || 1,
                    PageSize: params.pageSize || 10,
                    IsActive: params.IsActive || 1,
                    LanguageId: params.LanguageId || 2,
                    ...(params.searchText && { SearchText: params.searchText }),
                    ...(params.partCode && { PartCode: params.partCode }),
                    ...(params.partName && { PartName: params.partName })
                    
                }
            }),
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && response.data.items && Array.isArray(response.data.items)) {
                        return {
                            items: response.data.items,
                            totalCount: response.data.totalCount || 0
                        };
                    }
                    if (Array.isArray(response.data)) {
                        return {
                            items: response.data,
                            totalCount: response.data.length
                        };
                    }
                    // Eğer response direkt array ise
                    if (Array.isArray(response)) {
                        return {
                            items: response,
                            totalCount: response.length
                        };
                    }
                }
                return {
                    items: [],
                    totalCount: 0
                };
            },
            providesTags: (result) => (result?.items ? [...result.items.map(({ id }) => ({ type: 'ExternalItemDescription', id })), { type: 'ExternalItemDescription', id: 'LIST' }] : [{ type: 'ExternalItemDescription', id: 'LIST' }])
        }),
        createExternalItemDescription: builder.mutation({
            query: (data) => ({
                url: 'v1/parts/external-item-description',
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'ExternalItemDescription', id: 'LIST' }]
        }),
        updateExternalItemDescription: builder.mutation({
            query: (data) => ({
                url: 'v1/parts/external-item-description-update',
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: [{ type: 'ExternalItemDescription', id: 'LIST' }]
        }),
        deleteExternalItemDescription: builder.mutation({
            query: (data) => ({
                url: 'v1/parts/external-item-description-delete',
                method: 'DELETE',
                body: data
            }),
            invalidatesTags: [{ type: 'ExternalItemDescription', id: 'LIST' }]
        }),
        getRetailPaymentMethods: builder.query({
            query: ({ LanguageId }) => ({
                url: 'v1/dealerretailparts/payment_methods',
                method: 'GET',
                params: { LanguageId }
            }),
            transformResponse: (response) => response?.data ?? []
        }),
        getRetailList: builder.query({
            query: ({ PageNumber = 1, PageSize = 10, CustomerType = 0 }) => ({
                url: 'v1/dealerretailparts/get_retail_list',
                method: 'GET',
                params: { PageNumber, PageSize, CustomerType }
            }),
            transformResponse: (response) => ({
                items: response?.data?.items ?? [],
                totalCount: response?.data?.totalCount ?? 0
            }),
            providesTags: ['RetailList']
        }),
        getRetailPendingApproveList: builder.query({
            query: ({ PageNumber = 1, PageSize = 10 }) => ({
                url: 'v1/dealerretailparts/get_retail_pending_approve_list',
                method: 'GET',
                params: { PageNumber, PageSize }
            }),
            transformResponse: (response) => ({
                items: response?.data?.items ?? [],
                totalCount: response?.data?.totalCount ?? 0
            }),
            providesTags: ['RetailPendingList']
        }),
        approveOrRejectRetail: builder.mutation({
            query: (data) => ({
                url: 'v1/dealerretailparts/approve_or_reject_retail',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['RetailList', 'RetailPendingList']
        }),
        getRetailDetail: builder.query({
            query: ({ RetailId }) => ({
                url: 'v1/dealerretailparts/get_retail_detail',
                method: 'GET',
                params: { RetailId }
            }),
            transformResponse: (response) => response?.data ?? null,
            providesTags: (result, error, arg) => [{ type: 'RetailDetail', id: arg?.RetailId }]
        }),
        getRetailDetailPdf: builder.query({
            query: ({ RetailId }) => ({
                url: 'v1/DealerRetailParts/get_retail_detail_pdf',
                method: 'GET',
                params: { RetailId }
            }),
            transformResponse: (response) => response?.data ?? null
        }),
        searchPartForRetailList: builder.query({
            query: ({ LanguageId, WarehouseId, SearchText }) => ({
                url: 'v1/dealerretailparts/search_part_for_retail_list',
                method: 'GET',
                params: { LanguageId, WarehouseId, SearchText }
            }),
            transformResponse: (response) => response?.data?.items ?? []
        }),
        searchPartForRetail: builder.query({
            query: ({ WarehouseId, LanguageId, PartCode }) => ({
                url: 'v1/dealerretailparts/search_part_for_retail',
                method: 'GET',
                params: { WarehouseId, LanguageId, PartCode }
            }),
            transformResponse: (response) => response?.data ?? null
        }),
        createDealerRetail: builder.mutation({
            query: (data) => ({
                url: 'v1/dealerretailparts/create_dealer_retail',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['RetailList']
        }),
        requestCancelDealerRetail: builder.mutation({
            query: (data) => ({
                url: 'v1/dealerretailparts/request_cancel_dealer_retail',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['RetailList', 'RetailPendingList', 'RetailPendingCancelList']
        }),
        approveOrRejectCancelRetail: builder.mutation({
            query: (data) => ({
                url: 'v1/dealerretailparts/approve_or_reject_cancel_retail',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['RetailList', 'RetailPendingList', 'RetailPendingCancelList']
        }),
        getRetailPendingCancelList: builder.query({
            query: ({ PageNumber = 1, PageSize = 10 }) => ({
                url: 'v1/dealerretailparts/get_retail_pending_cancel_list',
                method: 'GET',
                params: { PageNumber, PageSize }
            }),
            transformResponse: (response) => ({
                items: response?.data?.items ?? [],
                totalCount: response?.data?.totalCount ?? 0
            }),
            providesTags: ['RetailPendingCancelList']
        }),
        getRetailDealers: builder.query({
            query: (params) => ({
                url: 'v1/dealerretailparts/dealers',
                method: 'GET',
                params: {
                    SearchText: params?.SearchText || '',
                    RowCount: params?.RowCount || 50,
                    ...(params?.Id && params.Id > 0 ? { Id: params.Id } : {})
                }
            }),
            transformResponse: (response) => response?.data ?? []
        }),

        // Internal Retail Parts (Stock Transfer) Endpoints
        getInternalRetailPartsList: builder.query({
            query: (params) => ({
                url: 'v1/internalretailparts/get-list',
                method: 'GET',
                params: {
                    Kunnr: params?.Kunnr || '',
                    TransferType: params?.TransferType ?? 2,
                    AdminKunnr: params?.AdminKunnr ?? '',
                    PageNumber: params?.PageNumber || 1,
                    PageSize: params?.PageSize || 20
                }
            }),
            transformResponse: (response) => {
                if (response?.isSuccessful && response?.data) {
                    return {
                        items: response.data.items || [],
                        totalCount: response.data.totalCount || 0
                    };
                }
                return { items: [], totalCount: 0 };
            },
            providesTags: ['InternalRetailPartsList']
        }),
        getInternalRetailPartsDetail: builder.query({
            query: (transferId) => ({
                url: 'v1/internalretailparts/get-detail',
                method: 'GET',
                params: { TransferId: transferId }
            }),
            transformResponse: (response) => {
                if (response?.isSuccessful && response?.data) {
                    return response.data;
                }
                return [];
            },
            providesTags: ['InternalRetailPartsDetail']
        }),
        getInternalRetailSubDealers: builder.query({
            query: (params) => ({
                url: 'v1/internalretailparts/get-sub-dealers',
                method: 'GET',
                params: {
                    SearchText: params?.SearchText || '',
                    ...(params?.RowCount ? { RowCount: params.RowCount } : {})
                }
            }),
            transformResponse: (response) => {
                if (response?.isSuccessful && response?.data) {
                    return response.data.map(d => ({ label: d.name, value: d.id }));
                }
                return [];
            }
        }),
        getInternalRetailParts: builder.query({
            query: (params) => ({
                url: 'v1/internalretailparts/get-parts',
                method: 'POST',
                body: {
                    partCode: params?.PartCode || '',
                    customerId: params?.CustomerId || 0,
                    rowCount: params?.RowCount || 50,
                    selectedParts: params?.SelectedParts || []
                }
            }),
            transformResponse: (response) => {
                if (response?.isSuccessful && response?.data) {
                    return response.data;
                }
                return [];
            }
        }),
        createInternalRetailTransfer: builder.mutation({
            query: (data) => ({
                url: 'v1/internalretailparts/create',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['InternalRetailPartsList']
        }),
        getUnpickedParts: builder.query({
            query: (params) => {
                const queryParams = {
                    pageNumber: params?.pageNumber || 1,
                    pageSize: params?.pageSize || 10,
                    ...(typeof params?.isDomestic === 'boolean' && { isDomestic: params.isDomestic }),
                    ...(params?.dealerNo && { dealerNo: params.dealerNo }),
                    ...(params?.invoiceNo && { invoiceNo: params.invoiceNo }),
                    ...(params?.deliveryNo && { deliveryNo: params.deliveryNo }),
                    ...(params?.eFaturaNo && { eFaturaNo: params.eFaturaNo }),
                    ...(params?.languageId && { languageId: params.languageId })
                };
                return {
                    url: 'v1/InternalRetailParts/get-unpicked-parts',
                    method: 'GET',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                if (response?.isSuccessful && response?.data) {
                    return {
                        items: response.data.items || [],
                        totalCount: response.data.totalCount || 0
                    };
                }
                return { items: [], totalCount: 0 };
            },
            providesTags: ['UnpickedParts']
        }),
        getOrderPickupDetail: builder.query({
            query: ({ Matnr, OrdrNo, KlmNo, Dln, InvNo }) => ({
                url: 'v1/parts/order-pick-up-detail',
                method: 'GET',
                params: {
                    ...(Matnr ? { Matnr } : {}),
                    ...(OrdrNo ? { OrdrNo } : {}),
                    ...(KlmNo ? { KlmNo } : {}),
                    ...(Dln ? { Dln } : {}),
                    ...(InvNo ? { InvNo } : {})
                }
            })
        }),
        exportUnpickedPartsToExcel: builder.query({
            query: (params) => ({
                url: 'v1/InternalRetailParts/export-unpicked-parts-to-excel',
                method: 'POST',
                body: {
                    isDomestic: params?.isDomestic ?? null,
                    dealerNo: params?.dealerNo ?? null,
                    invoiceNo: params?.invoiceNo ?? null,
                    deliveryNo: params?.deliveryNo ?? null,
                    eFaturaNo: params?.eFaturaNo ?? null,
                    languageId: params?.languageId ?? 1
                }
            }),
            transformResponse: (response) => {
                if (response?.isSuccessful && response?.data) {
                    const { fileBase64, fileName, contentType, recordCount, fileSize, isCompressed, compressionType } = response.data;
                    return { fileBase64, fileName, contentType, recordCount, fileSize, isCompressed, compressionType };
                }
                throw new Error(response?.errorMessage || 'Export failed');
            }
        }),
        exportInterserviceToExcel: builder.query({
            query: (params) => ({
                url: 'v1/internalretailparts/export-interservice-to-excel',
                method: 'POST',
                body: {
                    kunnr: params.Kunnr ?? null,
                    adminKunnr: params.AdminKunnr ?? null,
                    languageId: params.languageId ?? 1
                }
            }),
            transformResponse: (response) => {
                if (response?.isSuccessful && response?.data) {
                    const { fileBase64, fileName, contentType, recordCount, fileSize, isCompressed, compressionType } = response.data;
                    return { fileBase64, fileName, contentType, recordCount, fileSize, isCompressed, compressionType };
                }
                throw new Error(response?.errorMessage || 'Export failed');
            }
        }),
        exportSapInvoicesToExcel: builder.query({
            queryFn: excelExportQuery('v1/parts/get-invoices-excel', ({ Received, InvoiceNo, efatura } = {}) => ({
                Received,
                ...(InvoiceNo ? { InvoiceNo } : {}),
                ...(efatura ? { efatura } : {})
            }))
        }),
        exportExternalInvoicesToExcel: builder.query({
            queryFn: excelExportQuery('v1/parts/get-external-invoices-excel')
        }),
        exportDeliveryNotePickupListToExcel: builder.query({
            queryFn: excelExportQuery('v1/parts/delivery-note-pickup-list-excel', ({ Received, Dln } = {}) => ({
                Received,
                ...(Dln ? { Dln } : {})
            }))
        })
    })
});

export const {
    useGetWaybillShipmentsQuery,
    useGetOrderBalancesQuery,
    useUserSearchPartQuery,
    useGetSapInvoiceDetailQuery,
    useGetSapInvoicesQuery,
    useInternalOrderPickupMutation,
    useGetExternalInvoicesQuery,
    useGetExternalInvoiceDetailQuery,
    useExternalOrderPickupMutation,
    useGetDeliveryNotePickupListQuery,
    useGetDeliveryNotePickupDetailQuery,
    useDeliveryNotePickupMutation,
    useGetExternalItemDescriptionsQuery,
    useCreateExternalItemDescriptionMutation,
    useUpdateExternalItemDescriptionMutation,
    useDeleteExternalItemDescriptionMutation,
    useGetRetailListQuery,
    useGetRetailDetailQuery,
    useLazyGetRetailDetailQuery,
    useLazyGetRetailDetailPdfQuery,
    useLazySearchPartForRetailQuery,
    useLazySearchPartForRetailListQuery,
    useCreateDealerRetailMutation,
    useGetRetailPaymentMethodsQuery,
    useGetRetailPendingApproveListQuery,
    useApproveOrRejectRetailMutation,
    useRequestCancelDealerRetailMutation,
    useApproveOrRejectCancelRetailMutation,
    useGetRetailPendingCancelListQuery,
    useGetRetailDealersQuery,
    // Internal Retail Parts (Stock Transfer) hooks
    useGetInternalRetailPartsListQuery,
    useGetInternalRetailPartsDetailQuery,
    useGetInternalRetailSubDealersQuery,
    useLazyGetInternalRetailPartsQuery,
    useCreateInternalRetailTransferMutation,
    useGetUnpickedPartsQuery,
    useLazyExportUnpickedPartsToExcelQuery,
    useLazyGetOrderPickupDetailQuery,
    useLazyExportInterserviceToExcelQuery,
    useLazyExportSapInvoicesToExcelQuery,
    useLazyExportExternalInvoicesToExcelQuery,
    useLazyExportDeliveryNotePickupListToExcelQuery
} = partsApi;
